import React, { useState } from 'react';
import { formatDublinDateTime } from '../../utils/date';
import { useParams } from 'react-router-dom';
import {
  CalendarDaysIcon,
  MapPinIcon,
  UserIcon,
  EyeIcon,
  ExclamationTriangleIcon,
  UserGroupIcon,
  PrinterIcon,
  PaperClipIcon,
} from '@heroicons/react/24/outline';
import {
  useGetIncidentQuery,
  useSetIncidentGroupIdMutation,
  useLocalGetLocationByIdQuery,
  useGetIncidentFieldsQuery,
} from '../../store/api/localApi';
import { useAuth } from '../../hooks/useAuth';
import { ScopedLink } from '../../components/ScopedLink';
import { BackLink } from '../../components/BackLink';
import IncidentSeverityChip from '../../components/incidents/IncidentSeverityChip';
import IncidentAmendmentList from '../../components/incidents/IncidentAmendmentList';
import IncidentTuslaPanel from '../../components/incidents/IncidentTuslaPanel';
import IncidentActions from '../../components/incidents/IncidentActions';
import IncidentNotificationStatus from '../../components/incidents/IncidentNotificationStatus';
import IncidentCreateModal, {
  IncidentCreatePrefill,
} from '../../components/incidents/IncidentCreateModal';
import { useToast } from '../../components/Toast';
import { IncidentSeverity, IncidentType, StaffIncidentStatus } from '../../types';
import './IncidentDetail.print.css';

const INCIDENT_TYPE_LABELS: Record<IncidentType, string> = {
  [IncidentType.ACCIDENT]: 'Accident',
  [IncidentType.INJURY]: 'Injury',
  [IncidentType.ILLNESS]: 'Illness',
  [IncidentType.MEDICATION_ERROR]: 'Medication error',
  [IncidentType.BEHAVIOURAL]: 'Behavioural',
  [IncidentType.OTHER]: 'Other',
};

function formatDateTime(iso: string): string {
  return formatDublinDateTime(iso);
}

/** Renders a customFields answer generically by value SHAPE (boolean →
 *  Yes/No, string/number as-is, anything else stringified) — deliberately
 *  type-agnostic rather than switching on the field's configured TYPE. A
 *  MULTI_SELECT answer is an array, so it still needs the generic
 *  JSON.stringify fallback; this keeps that one function correct for every
 *  allowlisted type without hand-rolling a formatter per type. */
function formatCustomFieldValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'string' || typeof value === 'number') return String(value);
  // MULTI_SELECT answers are string arrays — join for a readable printed
  // record rather than falling through to a raw JSON array literal.
  if (Array.isArray(value)) {
    return value.length > 0 ? value.map(String).join(', ') : '—';
  }
  return JSON.stringify(value);
}

/**
 * GET /incidents/{incidentId} — the full staff/admin detail. Everything the
 * PARENT detail view (`ParentChildIncidents`/`IncidentAcknowledgeCard`)
 * deliberately does NOT show lives here: amendments (the append-only
 * correction trail), per-guardian notification/acknowledgment status, the
 * Tusla panel for NOTIFIABLE incidents, witness identity, and `siblings`
 * (other children in the same group event). This page must never import
 * `toParentSafeIncident` or reuse a parent component — it consumes the
 * strictly-larger `StaffIncident`/`IncidentDetailResponse` shape.
 *
 * Withdrawn incidents are NOT hidden here (unlike the parent detail, which
 * 404s for a withdrawn record) — they render with an unmissable flag, same
 * principle as the register: a retraction stays in the statutory record.
 */
const IncidentDetail: React.FC = () => {
  const { incidentId } = useParams<{ incidentId: string }>();
  const { activeOrganizationId } = useAuth();
  const { data, isLoading, error } = useGetIncidentQuery(incidentId!, { skip: !incidentId });
  // Location name for the print header. Deliberately fetched by the
  // incident's OWN locationId, not the viewer's active-context location —
  // an org admin browsing across locations must still get the record's real
  // location on a printout, not wherever they currently happen to be
  // scoped. `GET /locations/{locationId}` is already the reusable,
  // location-access-gated endpoint every location-scoped page uses.
  const { data: location } = useLocalGetLocationByIdQuery(data?.incident.locationId ?? '', {
    skip: !data?.incident.locationId,
  });
  // Org-configured custom field labels (Task 24) — so this page can render
  // "Yard zone: North" instead of the raw stored key "yard_zone: North".
  // Never affects WHICH keys render (that's still driven by
  // incident.customFields itself, unaffected by config changes after the
  // fact) — only how each key is LABELLED.
  const { data: fieldsData } = useGetIncidentFieldsQuery(
    { organizationId: data?.incident.organizationId ?? '' },
    { skip: !data?.incident.organizationId }
  );
  const customFieldLabels = Object.fromEntries(
    (fieldsData?.config.fields ?? []).map((f) => [f.key, f.label])
  );
  const [setIncidentGroupId] = useSetIncidentGroupIdMutation();
  const { showError } = useToast();
  const [recordAnotherOpen, setRecordAnotherOpen] = useState(false);
  const [pendingGroupId, setPendingGroupId] = useState<string | null>(null);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div
        className="min-h-screen flex flex-col items-center justify-center gap-3 text-sm text-slate-500"
        data-testid="incident-detail-page-error"
      >
        <ExclamationTriangleIcon className="h-10 w-10 text-red-400" />
        <p className="text-base font-medium text-slate-700">Failed to load incident.</p>
      </div>
    );
  }

  const { incident, amendments, siblings, deliveries } = data;
  const isWithdrawn = incident.status === StaffIncidentStatus.WITHDRAWN;
  const isNotifiable = incident.severity === IncidentSeverity.NOTIFIABLE;

  // If the source incident has no group id yet, generating one retroactively
  // joins both records into a group — so the first record does not need to
  // know in advance that a second is coming. Generating the UUID alone does
  // NOT retroactively join anything on its own: this incident's own row must
  // ALSO be patched with the same id (via handleRecordAnother below), or its
  // `siblings` lookup (keyed on its OWN incidentGroupId field) stays empty
  // forever even though the new record correctly carries the group id.
  const prefill: IncidentCreatePrefill = {
    incidentGroupId: pendingGroupId ?? incident.incidentGroupId ?? crypto.randomUUID(),
    occurredAt: incident.occurredAt,
    locationDetail: incident.locationDetail,
    description: incident.description,
    incidentType: incident.incidentType,
    severity: incident.severity,
    // Deliberately NOT copied — these are per child:
    // childId, injuryDetail, actionTaken, attachments
  };

  return (
    <div className="py-6" data-testid="incident-detail-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 space-y-5">
        {/* Browser-only action bar — back link + print trigger. Hidden from
            the print stylesheet (incident-print-only-hide) so an inspector's
            copy never shows an unclickable "Back to register" link or a
            "Print" button that does nothing on paper. */}
        <div className="incident-print-only-hide flex items-center justify-between">
          {/* Was a bare history.back() with preventDefault, which is wrong for
              the way this page is actually reached: a parent opens an incident
              from a PUSH NOTIFICATION, so it is the first history entry and
              there is nothing to pop — back did nothing at all. The `to` was
              also /locations, which is not this page's parent.

              BackLink pops when there IS history (keeping the register filters
              the old comment rightly wanted to preserve) and otherwise goes to
              the incident register. */}
          <BackLink
            fallbackTo="/incidents"
            label="Back to register"
            data-testid="incident-detail-back"
          />

          <button
            type="button"
            data-testid="incident-print-btn"
            onClick={() => window.print()}
            className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <PrinterIcon className="h-4 w-4" aria-hidden="true" />
            Print
          </button>
        </div>

        {/* Admin-only lifecycle actions (submit/discard/close/withdraw/change
            severity), gated by the incident's current status. Renders nothing
            for a non-admin viewer or a terminal (withdrawn) record — so this
            row simply disappears rather than showing an empty toolbar. */}
        <IncidentActions incident={incident} />

        {/* Print-only header. Never rendered on screen (incident-print-only
            is display:none outside @media print) — the printed page needs
            its own self-contained heading (location + child + generated-at)
            since the on-screen back link/breadcrumb/nav are all stripped. */}
        <div
          data-testid="incident-print-header"
          className="incident-print-only hidden border-b-2 border-gray-800 pb-3"
        >
          <h1 className="text-lg font-bold text-gray-900">Incident Record</h1>
          <dl className="mt-1 text-sm text-gray-700 grid grid-cols-2 gap-x-4">
            <div>
              <dt className="inline font-medium">Child: </dt>
              <dd className="inline" data-testid="incident-print-child-name">
                {incident.childName}
              </dd>
            </div>
            <div>
              <dt className="inline font-medium">Location: </dt>
              <dd className="inline" data-testid="incident-print-location-name">
                {location?.name ?? incident.locationId}
              </dd>
            </div>
            <div>
              <dt className="inline font-medium">Incident ID: </dt>
              <dd className="inline">{incident.id}</dd>
            </div>
            <div>
              <dt className="inline font-medium">Printed: </dt>
              <dd className="inline">{formatDateTime(new Date().toISOString())}</dd>
            </div>
          </dl>
        </div>

        {isWithdrawn && (
          <div
            data-testid="incident-detail-withdrawn-banner"
            role="alert"
            className="rounded-md border border-gray-300 bg-gray-100 px-4 py-3 text-sm text-gray-800"
          >
            <p className="font-semibold">This incident has been withdrawn</p>
            {incident.withdrawnReason && (
              <p className="mt-0.5">Reason: {incident.withdrawnReason}</p>
            )}
            {incident.withdrawnAt && (
              <p className="mt-0.5 text-xs text-gray-500">
                Withdrawn {formatDateTime(incident.withdrawnAt)}
              </p>
            )}
          </div>
        )}

        {siblings.length > 0 && (
          <div
            data-testid="incident-sibling-banner"
            // Screen-only. See the print-only note below for why: naming
            // another family's child on a document that is nominally THIS
            // child's incident record is a real disclosure once the page can
            // be photocopied or filed, and an inspector auditing one child's
            // record has no need for a cross-reference to another family's
            // file — that navigation aid only makes sense in this
            // access-controlled, logged-in screen.
            className="incident-print-only-hide rounded-md border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800 flex items-start gap-2"
          >
            <UserGroupIcon className="h-5 w-5 flex-shrink-0 text-blue-500" aria-hidden="true" />
            <div>
              <p className="font-medium">
                Part of an incident involving {siblings.length + 1} children
              </p>
              <ul className="mt-1 flex flex-wrap gap-x-3 gap-y-1">
                {siblings.map((s) => (
                  <li key={s.id}>
                    <ScopedLink
                      to={`/incidents/${s.id}`}
                      data-testid={`incident-sibling-link-${s.id}`}
                      className="underline hover:text-blue-900"
                    >
                      {s.childName}
                    </ScopedLink>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {siblings.length > 0 && (
          <p
            data-testid="incident-print-sibling-note"
            className="incident-print-only hidden text-xs text-gray-500 italic"
          >
            This incident was recorded as part of a single event involving other children. Their
            records are held separately and are not named here.
          </p>
        )}

        <div
          className={`bg-white shadow rounded-lg overflow-hidden ${
            isWithdrawn ? 'opacity-75' : ''
          }`}
          data-testid="incident-detail-card"
        >
          <div className="px-6 py-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <h1 className="text-lg font-bold text-gray-900">
                  {INCIDENT_TYPE_LABELS[incident.incidentType]}
                </h1>
                <p className="text-sm text-gray-500">About {incident.childName}</p>
              </div>
              <div className="flex items-center gap-2">
                {isWithdrawn && (
                  <span
                    data-testid="incident-detail-withdrawn-flag"
                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-gray-200 text-gray-700"
                  >
                    Withdrawn
                  </span>
                )}
                <IncidentSeverityChip
                  severity={incident.severity}
                  testId="incident-detail-severity"
                />
              </div>
            </div>

            <dl className="mt-4 grid grid-cols-1 gap-y-2 sm:grid-cols-2 text-sm text-gray-600">
              <div className="flex items-center gap-1.5">
                <CalendarDaysIcon
                  className="h-4 w-4 text-gray-400 flex-shrink-0"
                  aria-hidden="true"
                />
                <dt className="font-medium text-gray-700">Occurred:</dt>
                <dd data-testid="incident-detail-occurred-at">
                  {formatDateTime(incident.occurredAt)}
                </dd>
              </div>
              <div className="flex items-center gap-1.5">
                <MapPinIcon className="h-4 w-4 text-gray-400 flex-shrink-0" aria-hidden="true" />
                <dt className="font-medium text-gray-700">Where:</dt>
                <dd data-testid="incident-detail-location">{incident.locationDetail}</dd>
              </div>
              <div className="flex items-center gap-1.5">
                <UserIcon className="h-4 w-4 text-gray-400 flex-shrink-0" aria-hidden="true" />
                <dt className="font-medium text-gray-700">Reported by:</dt>
                <dd data-testid="incident-detail-reported-by">{incident.reportedByStaffName}</dd>
              </div>
              {/* Witness identity — staff-only, never sent to a parent. */}
              {incident.witnessStaffName && (
                <div className="flex items-center gap-1.5">
                  <EyeIcon className="h-4 w-4 text-gray-400 flex-shrink-0" aria-hidden="true" />
                  <dt className="font-medium text-gray-700">Witness:</dt>
                  <dd data-testid="incident-detail-witness">{incident.witnessStaffName}</dd>
                </div>
              )}
            </dl>
          </div>

          <div className="border-t border-gray-100 px-6 py-5 space-y-4">
            <div>
              <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
                What happened
              </h2>
              <p
                className="text-sm text-gray-800 whitespace-pre-wrap"
                data-testid="incident-detail-description"
              >
                {incident.description}
              </p>
            </div>

            {incident.injuryDetail && (
              <div>
                <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
                  Injury
                </h2>
                <p
                  className="text-sm text-gray-800 whitespace-pre-wrap"
                  data-testid="incident-detail-injury"
                >
                  {incident.injuryDetail}
                </p>
              </div>
            )}

            {incident.actionTaken && (
              <div>
                <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
                  Action taken
                </h2>
                <p
                  className="text-sm text-gray-800 whitespace-pre-wrap"
                  data-testid="incident-detail-action-taken"
                >
                  {incident.actionTaken}
                </p>
              </div>
            )}

            {incident.followUp && (
              <div>
                <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
                  Follow-up
                </h2>
                <p
                  className="text-sm text-gray-800 whitespace-pre-wrap"
                  data-testid="incident-detail-follow-up"
                >
                  {incident.followUp}
                </p>
              </div>
            )}

            {/* Custom fields — Task 24 owns the config (labels/types/order).
                The KEY SET rendered is always incident.customFields itself
                (what was actually answered at the time), never the current
                config's field list — a field removed from the config later
                must not make an already-recorded answer disappear from the
                statutory record. `customFieldLabels` only affects the
                DISPLAY LABEL for a known key; an unrecognised/legacy key
                (config changed since this incident was filed) falls back to
                the raw stored key so nothing is silently hidden. */}
            {Object.keys(incident.customFields ?? {}).length > 0 && (
              <div data-testid="incident-detail-custom-fields">
                <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
                  Additional details
                </h2>
                <dl className="space-y-1">
                  {Object.entries(incident.customFields).map(([key, value]) => (
                    <div key={key} className="flex gap-1.5 text-sm">
                      <dt className="font-medium text-gray-700">
                        {customFieldLabels[key] ?? key}:
                      </dt>
                      <dd
                        className="text-gray-800 whitespace-pre-wrap"
                        data-testid={`incident-detail-custom-field-${key}`}
                      >
                        {formatCustomFieldValue(value)}
                      </dd>
                    </div>
                  ))}
                </dl>
              </div>
            )}
          </div>

          {incident.attachments.length > 0 && (
            <div className="border-t border-gray-100 px-6 py-5" data-testid="incident-attachments">
              <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-3">
                Photos / documents
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 incident-print-attachment-grid">
                {incident.attachments.map((a, idx) =>
                  a.url ? (
                    <a
                      key={a.s3Key}
                      href={a.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`incident-detail-attachment-${a.s3Key}`}
                      className="incident-print-attachment block rounded overflow-hidden border border-gray-200 hover:opacity-90"
                    >
                      {a.mimeType.startsWith('image/') ? (
                        <img
                          src={a.url}
                          alt={`Incident attachment ${idx + 1}`}
                          className="incident-print-attachment-image object-cover w-full h-32"
                        />
                      ) : (
                        <div className="incident-print-attachment-reference h-32 flex flex-col items-center justify-center gap-1 text-xs text-gray-500 px-2 text-center">
                          <PaperClipIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                          <span>
                            Attachment {idx + 1}
                            <br />
                            {a.mimeType}
                          </span>
                        </div>
                      )}
                    </a>
                  ) : null
                )}
              </div>
            </div>
          )}

          <div className="incident-print-only-hide border-t border-gray-100 px-6 py-4 bg-gray-50 flex items-center justify-end">
            <button
              type="button"
              data-testid="incident-record-for-another-child"
              onClick={async () => {
                // Backfill THIS incident's own incidentGroupId first, if it
                // doesn't have one — otherwise the new record below would be
                // the only row carrying the group id, and this incident's own
                // siblings lookup would never find it (see the comment on
                // `prefill` above).
                if (!incident.incidentGroupId) {
                  const freshGroupId = crypto.randomUUID();
                  try {
                    await setIncidentGroupId({
                      incidentId: incident.id,
                      incidentGroupId: freshGroupId,
                    }).unwrap();
                    setPendingGroupId(freshGroupId);
                  } catch {
                    showError('Failed to link this incident to the new record. Please try again.');
                    return;
                  }
                }
                setRecordAnotherOpen(true);
              }}
              className="inline-flex items-center gap-1.5 rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
            >
              Record for another child
            </button>
          </div>
        </div>

        {isNotifiable && <IncidentTuslaPanel incident={incident} />}

        <IncidentNotificationStatus deliveries={deliveries} />

        <IncidentAmendmentList incidentId={incident.id} amendments={amendments} />
      </div>

      {recordAnotherOpen && activeOrganizationId && (
        <IncidentCreateModal
          open
          onClose={() => setRecordAnotherOpen(false)}
          locationId={incident.locationId}
          organizationId={incident.organizationId ?? activeOrganizationId}
          prefill={prefill}
        />
      )}
    </div>
  );
};

export default IncidentDetail;
